/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { InputColumn, TableValueDataData } from '../form/input/table/InputInterface';
import UniversalTracker from '../../../model/UniversalTracker';
import { FileTypes, NewEvidenceFile } from '../../../types/file';
import { HistoryDocument } from '../../../types/document';
import { SurveyActionData, SurveyModelMinimalUtrv, UnitConfig } from '../../../model/surveyData';
import { UniversalTrackerValuePlain } from '../../../types/surveyScope';
import { SafeUser } from '../../../constants/users';
import { ErrorMessageType } from '../form/input/InputProps';
import { EditorState } from 'lexical';

type FileStatus = 'updated';

export interface ExistingEvidenceFile extends HistoryDocument {
  status: FileStatus | string;
  type: FileTypes;
  link?: string;
  isDeleted?: boolean;
  isUpdated?: boolean;
}

export type EvidenceFile = ExistingEvidenceFile | NewEvidenceFile;

export interface DisplayCheckBox {
  [key: string]: boolean
}

export enum RowStatus {
  removed = 'removed',
  edited = 'edited',
  added = 'added',
  original = 'original',
}

export interface RowDataInfo<T = InputColumn[]> {
  id: number;
  rowStatus: RowStatus;
  isRemoved?: boolean;
  isEdited?: boolean;
  hasChanged?: boolean;
  data: T;
}

export interface TableDataInfo {
  editRowId: number,
  rows: RowDataInfo[]
}

interface Input<T extends ValueDataData = ValueDataData> {
  data?: T;
  table?: TableValueDataData;
  value?: number;
  unit?: string;
  numberScale?: string;
}

type ValueType = number | string | undefined;
export type ValueDataObject = Record<string, ValueType>
export type ValueDataData = string | string[] | ValueDataObject

export interface ValueData<T extends ValueDataData = ValueDataData> {
  data?: T;
  table?: TableValueDataData;
  explain?: string;
  isImported?: boolean;
  notApplicableType?: string;
  input?: Input<T>;
}

export interface PrepareSubmitDataParams {
  notApplicableType: string;
  comments: string;
  editorState?: EditorState;
  numberScale: string | undefined;
  valueData: ValueData;
  utr: UniversalTracker;
  unit: string | undefined;
  autoVerify: boolean;
  utrv: SurveyModelMinimalUtrv;
  files: EvidenceFile[];
  displayCheckbox: DisplayCheckBox;
  value: any;
  table: TableDataInfo
}

export interface InitialResettableQuestionState {
  loaded: boolean;
  displayCheckbox: DisplayCheckBox;
  displayConfirmation: boolean;
  action: string;
  saving: boolean;
  errored?: boolean;
  message?: string;
  inputMessage?: ErrorMessageType;
  comments: string; // old plain text note
  editorState?: EditorState; // rich text note
  files: NewEvidenceFile[];
  existingFiles: ExistingEvidenceFile[];
  details: { loaded: boolean, data: any };
  canContribute: boolean;
  canVerify: boolean;
  isNA: boolean;
  value: undefined | number;
  valueData: ValueData;
  table: TableDataInfo;
  /** @deprecated no longer used? */ 
  alternativeCode: string;
  alternativeCodeOverride: string;
  numberScale?: string,
  unit?: string,
  saveId?: string;
}

export interface AssuranceState {
  assurancePortfolio?: { assurances?: any[] };
  assurancePortfolioLoaded: boolean;
  hasPortfolio: boolean;
}

export interface QuestionReducerState extends InitialResettableQuestionState, AssuranceState {
}

export type GlossaryState = Record<string, { html: string }> | undefined;

export interface QuestionProps extends QuestionReducerState {
  // blueprint: Blueprint,
  index: number;
  hasChanged: boolean;
  utr?: UniversalTracker,
  utrv?: SurveyModelMinimalUtrv,
  isContributor: boolean;
  isVerifier: boolean;
  isOrganizationManager: boolean;
  survey: SurveyActionData;
  update: (data: any) => void;
  updateTable: (table: Partial<TableDataInfo>, error?: ErrorMessageType) => any;
  scrollToRef: () => void;
  glossaryState?: GlossaryState;
  // Redux
  errored?: boolean;
  isAggregate: boolean;

  handleNA?: () => void;
  handleNR?: () => void;
  handleAssuranceAction?: (action: LockAction) => void;
  handleReject?: () => void;
  handleComments?: (comments: string) => void;
}

export interface QuestionState {
  errored: boolean;
  message?: string;
}

export interface QuestionChangeMinProps {
  utr: UniversalTracker;
  utrv: Pick<UniversalTrackerValuePlain, '_id' | 'initiativeId' | 'valueData' | 'stakeholders' | 'status'>;
}

export interface QuestionChangeOptionalProps {
  utr?: UniversalTracker;
  utrv?: Pick<UniversalTrackerValuePlain, '_id' | 'valueData' | 'value' | 'status'>;
  surveyData?: { unitConfig?: UnitConfig } | {},
}

export interface ChangeQuestionProps extends QuestionChangeMinProps {
  surveyData: Pick<SurveyActionData, '_id' | 'roles' | 'stakeholders' | 'initiativeId' | 'unitConfig'>;
  user: Pick<SafeUser, '_id' | 'permissions'>;
}

interface SubmitValueData extends ValueData {
  input: Input
}

export interface QuestionSubmitData {
  autoVerify: 'true' | '';
  files: EvidenceFile[];
  comments: string;
  editorState?: EditorState,
  value?: string | number;
  valueData: SubmitValueData;
  unit?: string;
  numberScale?: string;
}

export interface QuestionSubmitFormData {
  autoVerify: 'true' | '';
  existingEvidence: ExistingEvidenceFile[];
  files?: File[];
  evidenceLinks?: EvidenceFile[];
  note?: string;
  editorState?: string;
  value?: string | number;
  valueData?: SubmitValueData;
  unit?: string;
  numberScale?: string;
  filesDescriptions?: string[];
}

export enum LockAction {
  Close = 'close',
  Open = 'open',
}
